import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '~store/hooks/store';
import { AppState } from '~store/modules';

export const selectCatalog = createSelector(
  (state: AppState) => state,
  (state) => {
    if (state.catalog.state === 'loaded') {
      return state.catalog.model;
    }
    return null;
  },
);

export const selectCatalogState = createSelector(
  (state: AppState) => state,
  ({ catalog }) => catalog.state,
);

export const selectDataclasses = createSelector(selectCatalog, (model) => {
  if (model) return model.dataClasses;
  return [];
});

export const selectSingletons = createSelector(selectCatalog, (model) => {
  if (model) return model.singletons;
  return [];
});

export const selectVirtualDataClasses = createSelector(
  selectDataclasses,
  (dataClasses) => {
    return Object.values(dataClasses).filter(({ virtual }) => virtual);
  },
);

export const selectCatalogMethods = createSelector(selectCatalog, (model) => {
  if (model) {
    return model.methods;
  }
  return [];
});

const selectMethodList = (dataclassId?: string) =>
  createSelector(
    (state: AppState) => state,
    (state) => {
      switch (state.catalog.state) {
        case 'loaded':
          const target = dataclassId
            ? state.catalog.model.dataClasses[dataclassId]
            : state.catalog.model;
          return target?.methods || [];
        default:
          return [];
      }
    },
  );

export const useMethods = (dataclassId?: string) => {
  return useAppSelector(selectMethodList(dataclassId), shallowEqual);
};

const selectMethod = (dataclassId: string, methodId: string) =>
  createSelector(selectMethodList(dataclassId), (list) =>
    list.find((method) => `${method.applyTo}.${method.name}` === methodId),
  );
export const useMethod = (
  dataclassId: string,
  methodId: string,
  applyTo: catalog.ApplyTo,
) => {
  return useAppSelector(
    selectMethod(dataclassId, `${applyTo}.${methodId}`),
    shallowEqual,
  );
};
